/* eslint-disable import/no-cycle */
import axios from 'axios';
import adapter from 'axios/lib/adapters/http';
import i18next from 'i18next';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { StatusCodes } from 'http-status-codes';
import { sessionProvider } from 'utils';

import appConfig from 'config';
import store from 'store/store';

const client = axios.create({
  baseURL: appConfig.apiUrl,
});

client.interceptors.request.use((config) => {
  // eslint-disable-next-line no-param-reassign
  config.headers['Content-Language'] = i18next.language;
  return config;
});

// Axios middleware to convert all api responses to camelCase
client.interceptors.response.use((response) => {
  if (response.data && ['application/json', 'application/json; charset=utf-8'].includes(response.headers['content-type'])) {
    response.data = camelizeKeys(response.data);
  }
  return response;
});
// Axios middleware to convert all api requests to snake_case
client.interceptors.request.use((config) => {
  const newConfig = { ...config };
  if (newConfig.headers['Content-Type'] === 'multipart/form-data') return newConfig;
  if (config.params) {
    newConfig.params = decamelizeKeys(config.params);
  }
  if (config.data) {
    newConfig.data = decamelizeKeys(config.data);
  }
  return newConfig;
});

client.defaults.adapter = adapter;

client.interceptors.request.use((config) => {
  const jwt = sessionProvider.jwt();
  if (jwt && !(config.headers.Authorization === null)) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${jwt}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => {
    if (response && response.status === StatusCodes.UNAUTHORIZED) {
      sessionProvider.invalidate();
      store.dispatch({ type: 'ACCOUNT_LOGOUT' });
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === StatusCodes.UNAUTHORIZED) {
      sessionProvider.invalidate();
      store.dispatch({ type: 'ACCOUNT_LOGOUT' });
    }
    if (error?.response?.data instanceof Blob) {
      const blb = error?.response?.data;
      if (blb.type === 'application/json') {
        return blb.text().then((dat) => {
          // eslint-disable-next-line no-param-reassign
          error.response.data = camelizeKeys(JSON.parse(dat));
          return Promise.reject(error);
        });
      }
      return Promise.reject(error);
    }

    /* eslint-disable no-param-reassign */
    error.response = {
      ...error.response,
      data: camelizeKeys(error?.response?.data),
    };
    /* eslint-enable no-param-reassign */

    return Promise.reject(error);
  },
);

client.interceptors.request.use(
  (config) => {
    let oid;
    if (window && window.location) {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      oid = urlParams.get('oid');
    }
    if (!oid && appConfig.organizationId !== '') {
      oid = appConfig.organizationId;
    }

    if (oid) {
      if (!config.params) {
        // eslint-disable-next-line no-param-reassign
        config.params = {};
      }
      // eslint-disable-next-line no-param-reassign
      config.params.oid = oid;
      if (config.data) {
        // eslint-disable-next-line no-param-reassign
        config.data.oid = oid;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

export default client;
